import {useContext} from 'react'
import Banner from "./Banner";
import axios from 'axios'
import {ToastContext} from "../Toast";
import {useUser} from "../UserProvider";
import {useMessages} from "i18n";
import {ABOVE_SHEET_ATTR} from "components/CardSheet/cardSheetDismiss";
import {BannerRegion} from "./Banners.styles";

/**
 * Logged-in chrome banners, access-first (#12/#25): FREE is a legitimate subscription state (the
 * old "Payment incomplete" nag for every non-subscriber is gone — conversion happens via in-app
 * PRO labels/nudges), and account activation is nudged here. Billing problems no longer surface in
 * this region at all: the past-due warning lives in the account menu behind an avatar badge
 * (`nav/pro/ProfileMenu`, ui#1778), because a full-width floating banner over every page was more
 * chrome than the message warranted.
 *
 * Every banner renders inside {@link BannerRegion}, which floats the whole set over the page below
 * the absolute top bar (ui#1765) — one region for all of them, so no banner pushes content down and
 * none can hide under the bar (ui#1188).
 */
interface BannersProps {
	isLoggedIn: boolean;
	suppressActivation: boolean;
}

export default function Banners({isLoggedIn, suppressActivation}: BannersProps) {
	const m = useMessages();
	const {showErrorToast, showSuccessToast} = useContext(ToastContext);
	const [user] = useUser();

	const activateAccountByEmail = <Banner
		message={m.banner.activateAccount}
		actionOneMessage={m.banner.resendEmail}
		onActionOneClick={async () => {
			try {
				(await axios.post("/activate/resend-confirmation-email"));
				showSuccessToast(m.activate.resendAccepted);
			} catch {
				showErrorToast(m.banner.activationError)
			}
		}}
	/>;

	// ONE render site, in both auth states: a logged-out visitor gets the region holding nothing, which
	// costs nothing (it collapses — see Banners.styles). Two returns would be two copies of these
	// attributes, and the next one added would land on only one of them.
	//
	// `ABOVE_SHEET_ATTR` for the same reason the toast and the cookie banner carry it: the region now
	// floats OVER the docked card sheet, so a click here is the sender using the app's chrome, not the
	// page behind the sheet. Without it the sheet's light dismiss ate the click — the banner's action
	// closed the sheet and navigated home instead of opening the billing portal (ui#1188/ui#1534).
	return <BannerRegion data-banner-region {...{[ABOVE_SHEET_ATTR]: ""}}>
		{isLoggedIn && !suppressActivation && !user.isAccountActive() && activateAccountByEmail}
	</BannerRegion>;
}
